/**
 * mlistApi config — mlist-api backend (staff JWT, token auth).
 *
 * REACT_APP_MLIST_API_BASE_URL must be an origin or base URL **with protocol**:
 *   https://api.mlist.ai
 *   http://localhost:8000
 *   https://api.mlist.ai/api/v1   (api/v1 suffix optional)
 *
 * We never prepend "https:" or "https://" when a protocol is already present.
 */

const HAS_PROTOCOL_RE = /^https?:\/\//i;

/**
 * Strip accidental duplicate protocol prefixes (e.g. https:https://host).
 * @param {string} value
 */
function stripDuplicateProtocol(value) {
  let s = value;
  s = s.replace(/^https:(?=https:\/\/)/i, "");
  s = s.replace(/^http:(?=https:\/\/)/i, "");
  s = s.replace(/^https:(?=http:\/\/)/i, "");
  return s;
}

/**
 * Resolve mlist-api URLs from REACT_APP_MLIST_API_BASE_URL.
 * @param {string | undefined} rawEnv
 */
export function resolveMlistApiUrls(rawEnv) {
  const rawInput = String(rawEnv ?? "").trim();

  if (!rawInput) {
    return {
      configured: false,
      valid: false,
      errorMessage: "REACT_APP_MLIST_API_BASE_URL not configured",
      rawInput: "",
      normalizedBase: "",
      protocol: null,
      apiBaseUrl: "",
      tokenUrl: "",
    };
  }

  let normalizedBase = rawInput.replace(/\/+$/, "");
  normalizedBase = stripDuplicateProtocol(normalizedBase);

  if (/https?:https?:/i.test(normalizedBase)) {
    return {
      configured: true,
      valid: false,
      errorMessage: "Invalid MList API base URL (duplicate protocol)",
      rawInput,
      normalizedBase,
      protocol: null,
      apiBaseUrl: "",
      tokenUrl: "",
    };
  }

  if (!HAS_PROTOCOL_RE.test(normalizedBase)) {
    return {
      configured: true,
      valid: false,
      errorMessage:
        "Invalid MList API base URL (include http:// or https://; do not prefix protocol twice)",
      rawInput,
      normalizedBase,
      protocol: null,
      apiBaseUrl: "",
      tokenUrl: "",
    };
  }

  let parsed;
  try {
    parsed = new URL(normalizedBase);
  } catch {
    return {
      configured: true,
      valid: false,
      errorMessage: "Invalid MList API base URL",
      rawInput,
      normalizedBase,
      protocol: null,
      apiBaseUrl: "",
      tokenUrl: "",
    };
  }

  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    return {
      configured: true,
      valid: false,
      errorMessage: "Invalid MList API base URL (unsupported protocol)",
      rawInput,
      normalizedBase,
      protocol: parsed.protocol,
      apiBaseUrl: "",
      tokenUrl: "",
    };
  }

  let pathname = parsed.pathname.replace(/\/+$/, "");
  let apiPath;
  if (pathname.endsWith("/api/v1")) {
    apiPath = `${pathname}/`;
  } else if (pathname.endsWith("/v1")) {
    apiPath = `${pathname}/`;
  } else if (pathname === "" || pathname === "/") {
    apiPath = "/api/v1/";
  } else {
    apiPath = `${pathname}/api/v1/`;
  }

  const apiBaseUrl = `${parsed.origin}${apiPath}`;
  const tokenUrl = `${apiBaseUrl}auth/token/`;

  return {
    configured: true,
    valid: true,
    errorMessage: null,
    rawInput,
    normalizedBase: `${parsed.origin}${pathname === "" || pathname === "/" ? "" : pathname}`,
    protocol: parsed.protocol,
    apiBaseUrl,
    tokenUrl,
  };
}

const _resolved = resolveMlistApiUrls(process.env.REACT_APP_MLIST_API_BASE_URL);

export const MLIST_API_CONFIG = _resolved;

/** Base URL for relative paths (e.g. admin-tools/..., auth/token/). Ends with /api/v1/ */
export const MLIST_API_BASE_URL = _resolved.valid ? _resolved.apiBaseUrl : "";

export const MLIST_API_TOKEN_URL = _resolved.valid ? _resolved.tokenUrl : "";

export const isMlistApiConfigured = () => _resolved.configured;

export const isMlistApiValid = () => _resolved.valid;

export const getMlistApiConfig = () =>
  resolveMlistApiUrls(process.env.REACT_APP_MLIST_API_BASE_URL);
